import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

const VaultInsightRulesPage = React.lazy(() => import("./VaultInsightRulesPage"));
const VaultInsightRunsPage = React.lazy(() => import("./VaultInsightRunsPage"));
const VaultInsightRunDetailPage = React.lazy(() => import("./VaultInsightRunDetailPage"));

/**
 * Vault Insights Module Config
 *
 * Single source of truth for permissions: business-builder-backend/src/modules/vault-insights/permissions.ts.
 * Routes use `permissionLevel` ("view" / "edit" / "delete") which is resolved at runtime
 * from the backend's modulePermissions GraphQL query.
 *
 * Phase 0: scheduler runs in Tom's local backend (filesystem reader at VAULT_PATH).
 */
const vaultInsightsModuleConfig: ModuleConfig = {
  id: "vault-insights",
  name: "Vault Insights",
  description: "Self-service scheduled email/SMS digests rendered from your Obsidian vault",
  version: "0.1.0",
  enabled: true,
  category: "automation",
  order: 30,
  icon: "FiZap",
  color: "#805AD5",

  routes: [
    {
      path: "/vault-insights",
      component: VaultInsightRulesPage,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      path: "/vault-insights/rules",
      component: VaultInsightRulesPage,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      path: "/vault-insights/runs",
      component: VaultInsightRunsPage,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      path: "/vault-insights/runs/:id",
      component: VaultInsightRunDetailPage,
      permissionLevel: "view",
      useLayout: true,
    },
  ],

  navigation: [
    {
      label: "Digest Rules",
      path: "/vault-insights/rules",
      icon: "🔮",
      permissionLevel: "view",
    },
    {
      label: "Run history",
      path: "/vault-insights/runs",
      icon: "📊",
      permissionLevel: "view",
    },
  ],

  // Resolved from backend at runtime
  permissions: {
    view: [],
    create: [],
    edit: [],
    delete: [],
  },

  quickActions: [
    {
      label: "Digest Rules",
      path: "/vault-insights/rules",
      icon: "FiZap",
      description: "Configure scheduled vault-insights digests",
    },
  ],

  changelog: [
    {
      version: "0.1.0",
      releaseDate: "2026-04-29",
      title: "Phase 0 — Tom-only filesystem reader",
      features: [
        "VaultInsightRule entity (cadence, channels, recipient, section toggles)",
        "5-minute scheduler picks rules due to fire",
        "Filesystem reader walks Obsidian vault (daily logs, projects, action items, audio inbox)",
        "AI-curated narrative via Claude Opus 4.6 (logged to AiUsageLog)",
        "Direct Email/SMS send under tenant brand",
        "Fire-now button for instant test fires",
        "Run history audit (90-day TTL)",
      ],
      notes: "Phase 0 is Tom-only — scheduler only fires on a backend host where VAULT_PATH resolves.",
    },
  ],

  roadmap: [
    {
      title: "Phase 1: VaultMirror adapter",
      description: "Mongo-backed vault mirror so the prod scheduler can serve other tenants without filesystem access.",
      priority: "high" as const,
      status: "planned" as const,
      features: ["launchd daemon mirrors vault → Mongo on file change", "scheduler reads from VaultMirror collection"],
    },
    {
      title: "Mongo-derived sections",
      description: "Wire up GOAL_60K, PIPELINE, TODAY_TASKS, WHEEL_OF_LIFE_DELTA so the digest also reflects production data.",
      priority: "medium" as const,
      status: "planned" as const,
      features: ["$60K progress aggregator", "Pipeline snapshot", "Tasks due today", "Wheel-of-life delta"],
    },
    {
      title: "Run history drawer",
      description: "Per-rule run history showing rendered email body + AI cost.",
      priority: "low" as const,
      status: "planned" as const,
      features: [],
    },
  ],
};

export default vaultInsightsModuleConfig;
